/** One editor for creating AND editing a template, in the app's centred modal
 *  shell (ConnectorDialog, wide, two columns - like the report-template
 *  editor of item 63). The inline-start column holds what is asked: the
 *  question, and how the answer looks (six answer-type cards and the
 *  categories or the score range). The aside holds where and when: which
 *  conversations (the whole tree, chosen organizations in the connector
 *  library's organization tree, or chosen agents with their avatars), what the
 *  AI reads, and when it runs (the reports' own schedule editor - daily,
 *  weekly or monthly at an hour in the owner's time zone - with the next runs
 *  and what a run and a month cost at today's volume). The footer stays put.
 *  Changing what an answer depends on warns before it clears the stored
 *  answers (never a silent wipe); a rename or a schedule never touches them.
 *  Esc or Cancel with unsaved changes asks first; focus stays inside and
 *  returns to the opener (ConnectorDialog). */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Input, InputNumber, Modal, Switch } from 'antd';
import { ClockCircleOutlined, CloseOutlined, ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { useAuth } from '@/hooks/useAuth';
import { useFeature } from '@/hooks/useFeature';
import { ConnectorDialog, DialogColumn, DialogColumns } from '@modules/chatbots/components/ConnectorDialog';
import insightsService from '../../services/insightsService';
import type {
  AgentOption, CreateTemplatePayload, InsightTemplate, InsightsQuota, ResponseType, TemplateSchedulePreview,
  TenantNode,
} from '../../types/insights';
import { ScheduleFields, scheduleFields, type ScheduleValue } from '../reports/ScheduleFields';
import { ScopeChoice, scopeDraftOf, scopeOf, scopeProblem, type ScopeDraft } from './ScopeChoice';
import { TYPE_ICON, TYPE_ORDER, errorCode, errorMessage, formatCount, formatEstimate, typeName } from './templateParts';
import '../reports/Reports.css';

const { TextArea } = Input;
const MAX_LABELS = 50;
const MAX_LABEL_CHARS = 100;
const PREVIEW_DELAY_MS = 500;

type InputMode = 'summary' | 'full_conversation';

interface Draft extends ScopeDraft {
  name: string;
  question: string;
  description: string;
  type: ResponseType;
  labels: string[];
  labelDraft: string;
  allowNew: boolean;
  min: number;
  max: number;
  inputMode: InputMode;
  schedule: ScheduleValue;
}

/** Mounted only while open: every opening starts from `template`. */
interface Props {
  /** null: a new template. */
  template: InsightTemplate | null;
  tenantTree: TenantNode[];
  /** Agents grouped by name: `value` is every same-named agent's id, comma-joined. */
  agentOptions: AgentOption[];
  quota: InsightsQuota | null;
  onClose: () => void;
  /** `run`: the person asked to run it right after saving. */
  onSaved: (template: InsightTemplate, run: boolean) => void;
}

function toDraft(template: InsightTemplate | null, agentOptions: Props['agentOptions']): Draft {
  const options = template?.response_options || {};
  const scope = template?.scope || {};
  return {
    name: template?.template_name || '',
    question: template?.question || '',
    description: template?.template_description || '',
    type: template?.response_type || 'boolean',
    labels: Array.isArray(options.labels) ? options.labels.map(String) : [],
    labelDraft: '',
    allowNew: !!(options.allow_new_labels || options.allow_new),
    min: Number.isInteger(options.min) ? options.min : 1,
    max: Number.isInteger(options.max) ? options.max : 5,
    ...scopeDraftOf(scope, agentOptions),
    inputMode: template?.input_mode === 'full_conversation' ? 'full_conversation' : 'summary',
    schedule: {
      frequency: template?.frequency || 'none',
      weekdays: template?.weekdays || [],
      monthDay: template?.month_day || 1,
      runTime: template?.run_time || '06:00',
    },
  };
}

function labelsOf(d: Draft): string[] {
  const extra = d.labelDraft.trim();
  return extra && !d.labels.includes(extra) ? [...d.labels, extra] : d.labels;
}

function optionsOf(d: Draft): Record<string, unknown> {
  if (d.type === 'label' || d.type === 'multi_label') return { labels: labelsOf(d), allow_new_labels: d.allowNew };
  if (d.type === 'scale') return { min: d.min, max: d.max };
  return {};
}

/** Two options objects mean the same answer list or score range. */
function sameOptions(type: ResponseType, a: Record<string, any>, b: Record<string, any>): boolean {
  if (type === 'label' || type === 'multi_label') {
    return JSON.stringify(a.labels || []) === JSON.stringify(b.labels || [])
      && !!(a.allow_new_labels || a.allow_new) === !!(b.allow_new_labels || b.allow_new);
  }
  if (type === 'scale') return (a.min ?? 1) === (b.min ?? 1) && (a.max ?? 5) === (b.max ?? 5);
  return true;
}

function sameScope(a: Record<string, any>, b: Record<string, any>): boolean {
  const norm = (s: Record<string, any>) => JSON.stringify([
    [...(s.tenant_ids || [])].sort(), [...(s.chatbot_ids || [])].sort(),
  ]);
  return norm(a) === norm(b);
}

export function TemplateEditorWindow({ template, tenantTree, agentOptions, quota, onClose, onSaved }: Props) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const transcriptsOn = useFeature('insights.transcripts');
  const [modal, modalHolder] = Modal.useModal();
  const [draft, setDraft] = useState<Draft>(() => toDraft(template, agentOptions));
  const initial = useRef<string>(JSON.stringify(draft));
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [preview, setPreview] = useState<TemplateSchedulePreview | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setFieldErrors((e) => {
      const { [key as string]: _drop, ...rest } = e;
      return rest;
    });
  };

  const options = optionsOf(draft);
  const scope = scopeOf(draft);
  const schedule = scheduleFields(draft.schedule);
  const scheduled = schedule.frequency !== 'none';
  const dirty = JSON.stringify(draft) !== initial.current;

  // What the stored answers depend on (the server clears them when one changes).
  const changedAnswerFields = useMemo(() => {
    if (!template) return [] as string[];
    const changed: string[] = [];
    if (draft.question.trim() !== template.question) changed.push('question');
    if (draft.type !== template.response_type) changed.push('response_type');
    else if (!sameOptions(draft.type, options, template.response_options || {})) changed.push('response_options');
    if (draft.inputMode !== (template.input_mode === 'full_conversation' ? 'full_conversation' : 'summary')) {
      changed.push('input_mode');
    }
    if (!sameScope(scope, template.scope || {})) changed.push('scope');
    return changed;
  }, [template, draft, options, scope]);
  const clearsAnswers = !!template && changedAnswerFields.length > 0 && template.total_results_count > 0;

  // The schedule's next runs and its cost, for the template as it stands in the editor.
  const previewKey = JSON.stringify([draft.question, draft.type, options, draft.inputMode, scope, schedule]);
  useEffect(() => {
    if (!scheduled) return;
    let cancelled = false;
    setPreviewFailed(false);
    const timer = window.setTimeout(() => {
      insightsService.previewTemplateSchedule({
        template_id: template?.template_id, question: draft.question, response_type: draft.type,
        response_options: options, input_mode: draft.inputMode, scope, ...schedule,
      })
        .then((p) => { if (!cancelled) setPreview(p); })
        .catch(() => { if (!cancelled) { setPreview(null); setPreviewFailed(true); } });
    }, PREVIEW_DELAY_MS);
    return () => { cancelled = true; window.clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey, template?.template_id]);

  const limits = quota?.auto_trigger;
  const scheduleExempt = !!quota?.is_exempt || (limits?.limit ?? 0) < 0;
  const scheduleFull = !scheduleExempt && !!limits && !template?.auto_trigger && limits.used >= limits.limit;

  const ownerName = (template && tenantTree.find((n) => n.tenant_id === template.tenant_id)?.tenant_name)
    || user?.tenant_name || '';

  const addLabel = () => {
    const value = draft.labelDraft.trim();
    if (!value) return;
    if (value.length > MAX_LABEL_CHARS || draft.labels.length >= MAX_LABELS) {
      setFieldErrors((e) => ({ ...e, labels: t('insights.tpl.errors.BAD_LABELS') }));
      return;
    }
    setDraft((d) => ({ ...d, labels: d.labels.includes(value) ? d.labels : [...d.labels, value], labelDraft: '' }));
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!draft.name.trim()) errors.name = t('insights.tpl.errors.NAME_REQUIRED');
    if (!draft.question.trim()) errors.question = t('insights.tpl.errors.QUESTION_REQUIRED');
    if (draft.type === 'scale' && !(draft.min < draft.max)) errors.scale = t('insights.tpl.errors.BAD_SCALE');
    const labels = labelsOf(draft);
    if ((draft.type === 'label' || draft.type === 'multi_label')
        && (labels.length > MAX_LABELS || labels.some((l) => l.length > MAX_LABEL_CHARS))) {
      errors.labels = t('insights.tpl.errors.BAD_LABELS');
    }
    const scopeMissing = scopeProblem(draft);
    if (scopeMissing) errors.scope = t(scopeMissing);
    setFieldErrors(errors);
    const first = ['name', 'question', 'labels', 'scale', 'scope'].find((k) => errors[k]);
    if (first) document.getElementById(`tpl-field-${first}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    return Object.keys(errors).length === 0;
  };

  const save = async (run: boolean) => {
    setFormError(null);
    if (!validate()) return;
    const full: CreateTemplatePayload = {
      template_name: draft.name.trim(),
      template_description: draft.description.trim() || undefined,
      question: draft.question.trim(),
      response_type: draft.type,
      response_options: options,
      input_mode: draft.inputMode,
      scope,
      is_active: true,
      ...schedule,
    };
    setSaving(true);
    try {
      let saved: InsightTemplate;
      if (template) {
        // Only what changed: re-sending an unchanged question would still read as unchanged,
        // but an options object in another shape must never clear the answers by accident.
        const payload: Partial<CreateTemplatePayload> = {
          template_name: full.template_name,
          template_description: draft.description.trim(),
          ...schedule,
        };
        if (changedAnswerFields.includes('question')) payload.question = full.question;
        if (changedAnswerFields.includes('response_type') || changedAnswerFields.includes('response_options')) {
          payload.response_type = full.response_type;
          payload.response_options = full.response_options;
        }
        if (changedAnswerFields.includes('input_mode')) payload.input_mode = full.input_mode;
        if (changedAnswerFields.includes('scope')) payload.scope = full.scope;
        saved = await insightsService.updateTemplate(template.template_id, payload);
      } else {
        saved = await insightsService.createTemplate(full);
      }
      initial.current = JSON.stringify(draft);
      onSaved(saved, run);
    } catch (err) {
      const code = errorCode(err);
      const text = errorMessage(t, err);
      const field = code === 'DUPLICATE_TEMPLATE_NAME' || code === 'NAME_REQUIRED' ? 'name'
        : code === 'QUESTION_REQUIRED' ? 'question' : code === 'BAD_SCALE' ? 'scale'
          : code === 'BAD_LABELS' ? 'labels' : code === 'SCOPE_OUT_OF_REACH' ? 'scope'
            : code?.startsWith('SCHEDULE_') || code === 'DAILY_TEMPLATE_QUOTA' ? 'schedule' : null;
      if (field) setFieldErrors((e) => ({ ...e, [field]: text }));
      else setFormError(text);
    } finally {
      setSaving(false);
    }
  };

  const requestClose = () => {
    if (!dirty || saving) {
      onClose();
      return;
    }
    modal.confirm({
      title: t('insights.tpl.editor.discardTitle'),
      icon: <ExclamationCircleOutlined />,
      content: template
        ? t('insights.tpl.editor.discardBody', { name: template.template_name })
        : t('insights.tpl.editor.discardBodyNew'),
      okText: t('insights.tpl.editor.discard'),
      okButtonProps: { danger: true },
      cancelText: t('insights.tpl.editor.keepEditing'),
      onOk: onClose,
    });
  };

  const typeHelp: Partial<Record<ResponseType, string>> = {
    boolean: t('insights.tpl.editor.helpBoolean'),
    numeric: t('insights.tpl.editor.helpNumeric'),
    free_text: t('insights.tpl.editor.helpText'),
  };

  const notice = formError
    ? formError
    : clearsAnswers ? (
      <span className="tpl-notice tpl-notice--warn" role="status">
        <ExclamationCircleOutlined aria-hidden />
        <span>{t('insights.tpl.editor.clearsAnswers', {
          count: template!.total_results_count, n: formatCount(template!.total_results_count),
        })}</span>
      </span>
    ) : null;

  const fieldError = (key: string) => fieldErrors[key]
    ? <p id={`tpl-${key}-err`} className="tpl-field__error" role="alert">{fieldErrors[key]}</p> : null;
  const estimate = preview?.estimate;

  return (
    <ConnectorDialog
      title={template ? t('insights.tpl.editor.editTitle') : t('insights.tpl.editor.newTitle')}
      size="wide"
      onClose={requestClose}
      note={notice}
      noteTone={formError ? 'error' : 'muted'}
      actions={(
        <>
          <button type="button" className="cx-btn" onClick={requestClose}>{t('common.cancel')}</button>
          {(!template || clearsAnswers) && (
            <button type="button" className="cx-btn" onClick={() => save(true)} disabled={saving}
                    data-testid="template-save-run">
              {t('insights.tpl.editor.saveAndRun')}
            </button>
          )}
          <button type="button" className="cx-btn cx-btn--primary" onClick={() => save(false)} disabled={saving}
                  data-testid="template-save">
            {t('common.save')}
          </button>
        </>
      )}
      testId="template-editor"
    >
      {modalHolder}
      <DialogColumns>
        <DialogColumn>
          <section className="tpl-section" aria-labelledby="tpl-sec-question">
            <h3 id="tpl-sec-question" className="tpl-section__title"><span className="tpl-section__num">1</span>
              {t('insights.tpl.editor.secQuestion')}</h3>
            <div className="tpl-field" id="tpl-field-name">
              <label className="tpl-field__label" htmlFor="tpl-name">
                {t('insights.tpl.editor.name')}<span className="tpl-required" aria-hidden="true"> *</span>
              </label>
              <Input id="tpl-name" dir="auto" autoFocus value={draft.name} maxLength={200}
                     status={fieldErrors.name ? 'error' : ''}
                     placeholder={t('insights.tpl.editor.namePlaceholder')} onChange={(e) => set('name', e.target.value)}
                     aria-invalid={!!fieldErrors.name} aria-describedby={fieldErrors.name ? 'tpl-name-err' : undefined}
                     aria-required="true" />
              {fieldError('name')}
            </div>
            <div className="tpl-field" id="tpl-field-question">
              <label className="tpl-field__label" htmlFor="tpl-question">
                {t('insights.tpl.editor.question')}<span className="tpl-required" aria-hidden="true"> *</span>
              </label>
              <TextArea id="tpl-question" dir="auto" value={draft.question} autoSize={{ minRows: 2, maxRows: 6 }}
                        maxLength={2000} status={fieldErrors.question ? 'error' : ''}
                        placeholder={t('insights.tpl.editor.questionPlaceholder')}
                        onChange={(e) => set('question', e.target.value)} aria-required="true"
                        aria-invalid={!!fieldErrors.question}
                        aria-describedby={fieldErrors.question ? 'tpl-question-err' : 'tpl-question-help'} />
              {fieldErrors.question ? fieldError('question')
                : <p id="tpl-question-help" className="tpl-field__help">{t('insights.tpl.editor.questionHelp')}</p>}
            </div>
            <div className="tpl-field">
              <label className="tpl-field__label" htmlFor="tpl-desc">
                {t('insights.tpl.editor.description')} <span className="tpl-muted">{t('insights.tpl.editor.optional')}</span>
              </label>
              <Input id="tpl-desc" dir="auto" value={draft.description} maxLength={500}
                     placeholder={t('insights.tpl.editor.descriptionPlaceholder')}
                     onChange={(e) => set('description', e.target.value)} />
            </div>
          </section>

          <section className="tpl-section" aria-labelledby="tpl-sec-answer">
            <h3 id="tpl-sec-answer" className="tpl-section__title"><span className="tpl-section__num">2</span>
              {t('insights.tpl.editor.secAnswer')}</h3>
            <p className="tpl-field__help">{t('insights.tpl.editor.answerHelp')}</p>
            <div className="tpl-types" role="radiogroup" aria-label={t('insights.tpl.editor.secAnswer')}>
              {TYPE_ORDER.map((type) => (
                <label key={type} className={`tpl-type${draft.type === type ? ' is-selected' : ''}`}>
                  <input type="radio" name="tpl-type" value={type} checked={draft.type === type}
                         onChange={() => set('type', type)} />
                  <span className="tpl-type__head">
                    <span className="tpl-type__icon" aria-hidden="true">{TYPE_ICON[type]}</span>
                    <span className="tpl-type__name">{typeName(t, type)}</span>
                  </span>
                  <span className="tpl-type__example">{t(`insights.tpl.example.${type}`)}</span>
                  <span className={`tpl-mini tpl-mini--${type}`} aria-hidden="true">
                    <i /><i /><i />{(type === 'scale' || type === 'numeric') && <><i /><i /></>}
                  </span>
                </label>
              ))}
            </div>

            {(draft.type === 'label' || draft.type === 'multi_label') && (
              <div className="tpl-field tpl-type-config" id="tpl-field-labels">
                <label className="tpl-field__label" htmlFor="tpl-label-input">{t('insights.tpl.editor.categories')}</label>
                {draft.labels.length > 0 && (
                  <ul className="tpl-chips" aria-label={t('insights.tpl.editor.categories')}>
                    {draft.labels.map((label) => (
                      <li key={label} className="tpl-chip">
                        <span dir="auto">{label}</span>
                        <button type="button" className="tpl-icon-btn"
                                aria-label={t('insights.tpl.editor.removeCategory', { name: label })}
                                onClick={() => set('labels', draft.labels.filter((l) => l !== label))}>
                          <CloseOutlined aria-hidden />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="tpl-chip-input">
                  <Input id="tpl-label-input" dir="auto" value={draft.labelDraft} maxLength={MAX_LABEL_CHARS}
                         placeholder={t('insights.tpl.editor.categoryPlaceholder')}
                         onChange={(e) => set('labelDraft', e.target.value)}
                         onPressEnter={(e) => { e.preventDefault(); addLabel(); }}
                         status={fieldErrors.labels ? 'error' : ''} aria-describedby="tpl-label-hint" />
                  <button type="button" className="cx-btn" onClick={addLabel} disabled={!draft.labelDraft.trim()}>
                    <PlusOutlined aria-hidden />{t('insights.tpl.editor.add')}
                  </button>
                </div>
                {fieldErrors.labels ? fieldError('labels')
                  : <p id="tpl-label-hint" className="tpl-field__help">
                    {draft.labels.length === 0 && !draft.labelDraft.trim()
                      ? t(draft.type === 'multi_label' ? 'insights.tpl.editor.findsTopics' : 'insights.tpl.editor.namesCategory')
                      : t('insights.tpl.editor.categoryHint')}
                  </p>}
                <label className="tpl-switch-row">
                  <Switch size="small" checked={draft.allowNew} onChange={(v) => set('allowNew', v)} />
                  <span>
                    <span className="tpl-switch-row__title">{t('insights.tpl.editor.allowNew')}</span>
                    <span className="tpl-field__help">{t('insights.tpl.editor.allowNewHelp')}</span>
                  </span>
                </label>
              </div>
            )}

            {draft.type === 'scale' && (
              <div className="tpl-field tpl-type-config" id="tpl-field-scale">
                <div className="tpl-scale">
                  <label className="tpl-field__label">
                    {t('insights.tpl.editor.scaleFrom')}
                    <InputNumber value={draft.min} precision={0} min={0} max={99} status={fieldErrors.scale ? 'error' : ''}
                                 onChange={(v) => set('min', Number(v ?? 0))} />
                  </label>
                  <label className="tpl-field__label">
                    {t('insights.tpl.editor.scaleTo')}
                    <InputNumber value={draft.max} precision={0} min={1} max={100} status={fieldErrors.scale ? 'error' : ''}
                                 onChange={(v) => set('max', Number(v ?? 0))} />
                  </label>
                </div>
                {fieldErrors.scale ? fieldError('scale')
                  : <p className="tpl-field__help">{t('insights.tpl.editor.scaleHelp')}</p>}
              </div>
            )}

            {typeHelp[draft.type] && <p className="tpl-field__help tpl-type-config">{typeHelp[draft.type]}</p>}
          </section>
        </DialogColumn>

        <DialogColumn aside>
          <section className="tpl-section tpl-section--aside" aria-labelledby="tpl-sec-where" id="tpl-field-scope">
            <h3 id="tpl-sec-where" className="tpl-section__title"><span className="tpl-section__num">3</span>
              {t('insights.tpl.editor.secConversations')}</h3>
            <ScopeChoice
              value={draft} tenantTree={tenantTree} agentOptions={agentOptions} testIdPrefix="template-scope"
              onChange={(next) => {
                setDraft((d) => ({ ...d, ...next }));
                setFieldErrors(({ scope: _drop, ...rest }) => rest);
              }}
              allHelp={(
                <p className="tpl-field__help">
                  {ownerName ? t('insights.tpl.editor.scopeAll', { org: ownerName }) : t('insights.tpl.editor.scopeAllPlain')}
                </p>
              )}
            />
            {fieldError('scope')}

            <fieldset className="tpl-fieldset">
              <legend className="tpl-field__label">{t('insights.tpl.editor.aiReads')}</legend>
              {/* The Templates area's own choice cards (the Run dialog's). */}
              <div className="tpl-choices tpl-choices--tight" role="radiogroup" aria-label={t('insights.tpl.editor.aiReads')}>
                {([
                  ['summary', 'readSummary', 'readSummaryTag', t('insights.tpl.editor.readSummaryDesc'), true],
                  ['full_conversation', 'readFull', 'readFullTag',
                    transcriptsOn ? t('insights.tpl.editor.readFullDesc') : t('insights.tpl.editor.fullOff'), transcriptsOn],
                ] as [InputMode, string, string, string, boolean][]).map(([mode, title, tag, desc, enabled]) => (
                  <label key={mode} className={`tpl-choice${draft.inputMode === mode ? ' is-selected' : ''}${enabled ? '' : ' is-disabled'}`}>
                    <input type="radio" name="tpl-reads" value={mode} checked={draft.inputMode === mode}
                           disabled={!enabled} onChange={() => set('inputMode', mode)} />
                    <span className="tpl-choice__text">
                      <span className="tpl-choice__title">
                        {t(`insights.tpl.editor.${title}`)} <span className="tpl-muted">{t(`insights.tpl.editor.${tag}`)}</span>
                      </span>
                      <span className="tpl-choice__desc">{desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          </section>

          <section className="tpl-section tpl-section--aside" aria-labelledby="tpl-sec-when" id="tpl-field-schedule"
                   data-testid="template-schedule">
            <h3 id="tpl-sec-when" className="tpl-section__title"><span className="tpl-section__num">4</span>
              {t('insights.tpl.editor.secWhen')}</h3>
            <ScheduleFields
              idPrefix="tpl-schedule" value={draft.schedule} onChange={(v) => set('schedule', v)}
              zone={preview?.zone} nextRuns={scheduled ? preview?.next_runs : []}
              nextRunsLabel={t('insights.tpl.editor.nextRuns')}
              timingNote={t('insights.tpl.editor.timing', { time: draft.schedule.runTime })}
              byHandNote={t('insights.tpl.editor.byHandHelp')}
              byHandLabel={t('insights.tpl.editor.byHand')} />
            {scheduled && (
              <p className="tpl-field__help">{t('insights.tpl.editor.eachRunReads')}</p>
            )}
            {scheduled && scheduleFull && (
              <p className="tpl-field__help tpl-field__help--warn">
                {t('insights.tpl.editor.scheduleFull', { used: formatCount(limits!.used), limit: formatCount(limits!.limit) })}
              </p>
            )}
            {scheduled && (
              <p className="rp-estimate tpl-schedule-cost" aria-live="polite" data-testid="template-schedule-cost">
                <ClockCircleOutlined aria-hidden />
                {previewFailed ? t('insights.tpl.editor.scheduleCostUnknown')
                  : !estimate ? t('insights.tpl.editor.estimating')
                    : t('insights.tpl.editor.scheduleCost', {
                      run: formatEstimate(t, estimate.cost_per_run_usd),
                      month: formatEstimate(t, preview!.per_month_usd),
                      n: formatCount(Math.round(estimate.conversations_per_run)),
                      model: estimate.model_name,
                    })}
              </p>
            )}
            {scheduled && estimate?.capped && (
              <p className="tpl-field__help">{t('insights.tpl.editor.scheduleCapped', { limit: formatCount(estimate.run_limit) })}</p>
            )}
            {fieldError('schedule')}
          </section>
        </DialogColumn>
      </DialogColumns>
    </ConnectorDialog>
  );
}
